// 金额 + 币种复合输入组件（SForm componentClass="currencyInput" / ProTablePlusForm editRender="currencyInput" 双体系共用）
// value 契约：{ value: number|string, currency: string }（金额字段名 value，币种字段名 currency）
// 以 bedrock pro FieldCurrency（@bedrock/pro-field Currency）为参考：
//   - 编辑态/只读态均可展示币种（showCurrency 开关）
//   - 编辑态/只读态均可展示 unit 单位指示器（showUnitIndicator，万/十万/百万…，复刻 useUnitIndicator 阈值；绝对定位气泡浮于输入框下方，复刻 FieldCurrency bubble）
//   - 多 color 状态（color: yellow/black/red/green，复用 CurrencyType.less 既有 class）
//   - currencyBeforeAmount 控制「币种在金额前 / 币种在金额后」排布（编辑 + 只读均生效）
// 受控组件：value + onChange，onChange 抛 { value, currency } 完整对象，杜绝半更新中间态
// 编辑态币种前置（currencyBeforeAmount=true）：以 Select 为主、InputNumber 为右，Input.Group compact 合并为统一带边框整体
// （镜像 bedrock InputPrice 结构——InputPrice.js 用 Input.Group compact 组合 Select + Input；compact CSS 已支持 InputNumber）
// unit 气泡水平位置用运行时测量锚定金额首数字（镜像 InputPrice measureRef 隐藏测量 span + useEffect 计算 hintPosition）
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputNumber, Select } from '@bedrock/components';
import cx from 'classnames';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { currencyEnum, getCurrencyName } from '#utils/number';
import { isBlank } from '@bedrock/utils';
import { objectTransenum } from './utils';
import './CurrencyType.less';

/** unit 单位阈值（复刻 bedrock pro FieldCurrency useUnitIndicator，自大到小匹配首个达标档位） */
const UNIT_THRESHOLDS: { value: number; label: string }[] = [
  { value: 1000000000000, label: '万亿' },
  { value: 100000000000, label: '千亿' },
  { value: 10000000000, label: '百亿' },
  { value: 1000000000, label: '十亿' },
  { value: 100000000, label: '亿' },
  { value: 10000000, label: '千万' },
  { value: 1000000, label: '百万' },
  { value: 100000, label: '十万' },
  { value: 10000, label: '万' },
];

/** unit 单位气泡样式（绝对定位浮于输入框下方，复刻 FieldCurrency bubble 灰底圆角 + 上箭头） */
const UNIT_BUBBLE = {
  bg: '#E4E5EB',
  radius: 4,
  padding: 4,
  fontSize: 12,
  lineHeight: 12,
  /** 气泡与输入框/金额文本的垂直间距 */
  gap: 8,
  /** 下方预留空间（padding-bottom），占住气泡空间，防止外层 overflow:hidden 裁剪 */
  reserve: 26,
  /** 编辑态气泡水平偏移回退（未测量时，复刻 bubble-edit left: 15px） */
  leftEdit: 15,
  /** 只读态气泡水平偏移（复刻 bubble-read left: 4px） */
  leftRead: 4,
  /** 编辑态右锚回退（币种前置金额右对齐、未测量时）：气泡右缘距容器右缘的距离 */
  rightOffset: 24,
} as const;

/** 币种 Select 固定宽度（复刻 CurrencyType.less .currency-type-right 140px） */
const CURRENCY_SELECT_WIDTH = 110;

/** 统一判断值是否为 NaN 或其字符串形式（大小写不敏感），避免误伤合法数值字符串（如 "100,000"） */
const isNaNLike = (value: unknown): boolean =>
  typeof value === 'number'
    ? Number.isNaN(value)
    : typeof value === 'string' && value.toLowerCase() === 'nan';

/** 金额千分位格式化（保留 precision 小数位，复刻 FieldCurrency formatter，只读态用） */
function formatAmount(
  value: number | string | undefined,
  precision: number
): string {
  if (isBlank(value)) return '--';
  const fixed = Number(value).toFixed(precision);
  const [intPart, decPart] = fixed.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart ? `${formattedInt}.${decPart}` : formattedInt;
}

/**
 * 编辑态金额展示文本（与 InputNumber formatter 输出一致，供气泡运行时测量锚点）：
 * 先拦截 NaN/空值防 "NaN" 残留，再按 toFixed 四舍五入后插入千分位（对齐只读态 formatAmount）
 */
function formatEditAmount(
  value: number | string | undefined,
  toFixed: number
): string {
  if (
    value === undefined ||
    value === null ||
    value === '' ||
    isNaNLike(value)
  ) {
    return '';
  }
  return Number(String(value).replace(/,/g, ''))
    .toFixed(toFixed)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * unit 单位指示器容器（绝对定位气泡，浮于输入框/金额文本下方，复刻 @bedrock/pro-field Currency）：
 * - 外层 position:relative 作为定位上下文，加 padding-bottom 预留气泡空间（防止外层 overflow:hidden 裁剪）
 * - type='edit' 外层参与 flex 撑满（min-w-0 flex-1），气泡浮于输入框下方
 * - type='read' 外层 inline 展示，气泡浮于金额文本下方
 * - 编辑态气泡水平位置：镜像 InputPrice 运行时测量（隐藏 measureRef span 量文本宽 + input padding，
 *   把气泡中心对准金额首数字），测量不可用时回退固定偏移
 */
const UnitIndicatorWrapper: React.FC<{
  type: 'edit' | 'read';
  content: string;
  show: boolean;
  /** 编辑态气泡水平锚定：'left'（默认，币种在后金额在左）/ 'right'（币种前置金额右对齐） */
  bubbleAnchor?: 'left' | 'right';
  /** 编辑态金额展示文本（用于运行时测量首数字位置，锚定气泡） */
  measureText?: string;
  children: React.ReactNode;
}> = ({ type, content, show, bubbleAnchor, measureText, children }) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [hintPos, setHintPos] = useState<{ left?: number; right?: number }>({});

  const isRightAnchor = type === 'edit' && bubbleAnchor === 'right';

  // 镜像 InputPrice：隐藏测量 span 量「金额展示文本」各段宽度，把气泡中心对准金额首数字
  useEffect(() => {
    if (
      type !== 'edit' ||
      !show ||
      !content ||
      !measureRef.current ||
      !wrapperRef.current
    ) {
      setHintPos({});
      return;
    }
    const display = measureText ?? '';
    if (!display) {
      setHintPos({});
      return;
    }
    const firstDigitIndex = display.search(/\d/);
    if (firstDigitIndex === -1) {
      setHintPos({});
      return;
    }
    measureRef.current.textContent = display.slice(0, firstDigitIndex);
    const beforeWidth = measureRef.current.offsetWidth;
    measureRef.current.textContent = display[firstDigitIndex];
    const digitWidth = measureRef.current.offsetWidth;
    measureRef.current.textContent = display;
    const totalWidth = measureRef.current.offsetWidth;
    const inputEl =
      wrapperRef.current.querySelector<HTMLElement>('.rock-input');
    const inputPaddingRight = inputEl
      ? parseInt(getComputedStyle(inputEl).paddingRight, 10) || 12
      : 12;
    const inputPaddingLeft = inputEl
      ? parseInt(getComputedStyle(inputEl).paddingLeft, 10) || 12
      : 12;
    if (isRightAnchor) {
      // 右对齐（币种前置）：气泡中心对准首数字，距容器右缘 = 首数字右侧文本宽 + input 右 padding
      setHintPos({
        right: totalWidth - beforeWidth - digitWidth / 2 + inputPaddingRight,
      });
    } else {
      // 左对齐（币种在后，金额在左）：气泡中心对准首数字，距容器左缘 = input 左 padding + 首数字前文本宽
      setHintPos({ left: beforeWidth + digitWidth / 2 + inputPaddingLeft });
    }
  }, [type, show, content, measureText, isRightAnchor]);

  if (!show) return <>{children}</>;
  const hasContent = !!content;
  const bubbleLeft =
    type === 'edit' ? UNIT_BUBBLE.leftEdit : UNIT_BUBBLE.leftRead;
  const isRight = type === 'edit' && isRightAnchor;
  return (
    <span
      ref={wrapperRef}
      className={cx(
        'relative inline-flex items-center',
        type === 'edit' && 'min-w-0 flex-1'
      )}
      style={hasContent ? { paddingBottom: UNIT_BUBBLE.reserve } : undefined}
    >
      {/* 隐藏测量 span（与输入同字体，仅编辑态展示气泡时挂载） */}
      {type === 'edit' && hasContent ? (
        <span
          ref={measureRef}
          className="absolute"
          style={{
            visibility: 'hidden',
            whiteSpace: 'pre',
            pointerEvents: 'none',
            fontFamily: 'Arial',
            fontSize: 14,
          }}
        />
      ) : null}
      {children}
      {hasContent ? (
        <span
          className="absolute"
          style={{
            // 相对含 padding-bottom 的定位盒：100% 落在预留空间底部，
            // 扣回 reserve 再补 gap，即紧贴输入框/文本下方 gap 像素
            top: `calc(100% - ${UNIT_BUBBLE.reserve}px + ${UNIT_BUBBLE.gap}px)`,
            left: isRight
              ? undefined
              : hintPos.left !== undefined
                ? hintPos.left
                : bubbleLeft,
            right: isRight
              ? hintPos.right !== undefined
                ? hintPos.right
                : UNIT_BUBBLE.rightOffset
              : undefined,
            transform: isRight
              ? hintPos.right !== undefined
                ? 'translateX(50%)'
                : undefined
              : 'translateX(-50%)',
            background: UNIT_BUBBLE.bg,
            borderRadius: UNIT_BUBBLE.radius,
            padding: `${UNIT_BUBBLE.padding}px`,
            color: 'var(--text-color-1)',
            whiteSpace: 'nowrap',
            fontSize: UNIT_BUBBLE.fontSize,
            lineHeight: `${UNIT_BUBBLE.lineHeight}px`,
            zIndex: 1,
          }}
        >
          {content}
          {/* 上箭头（复刻参考 ::after 三角） */}
          <span
            className="absolute"
            style={{
              top: -4,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderBottom: `4px solid ${UNIT_BUBBLE.bg}`,
            }}
          />
        </span>
      ) : null}
    </span>
  );
};

export interface CurrencyInputValue {
  value?: number | string;
  currency?: string;
}

export interface CurrencyInputProps {
  value?: CurrencyInputValue;
  onChange?: (next: CurrencyInputValue) => void;
  /** 只读态 */
  view?: boolean;
  disabled?: boolean;
  /** 是否展示币种（Select / 币种文字） */
  showCurrency?: boolean;
  /** 币种选项（SForm Select 契约：{ value, name }） */
  currencyOptions?: { value: string; name: string }[];
  /** 金额小数位 */
  precision?: number;
  toFixed?: number;
  /** true=币种在金额前；false=币种在金额后（默认，对齐 CurrencyType 金额左币种右） */
  currencyBeforeAmount?: boolean;
  /** 颜色状态：yellow/black/red/green，复用 CurrencyType.less 既有 class */
  color?: 'yellow' | 'black' | 'red' | 'green';
  /** 金额是否加粗（默认 true，对齐 FieldCurrency Read highlight） */
  highlight?: boolean;
  /** 编辑态 + 只读态展示 unit 单位气泡 */
  showUnitIndicator?: boolean;
  /** 单位气泡对齐：left=与金额/输入框左边缘对齐（间距 2px，走查要求）；center=left 偏移 + 水平居中（默认） */
  unitIndicatorAlign?: 'left' | 'center';
  /** 币种变化回调（外部接管，用于选币种自动带汇率等联动） */
  onCurrencyChange?: (currency: string) => void;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  className?: string;
  inputProps?: Partial<React.ComponentProps<typeof InputNumber>>;
  selectProps?: Partial<React.ComponentProps<typeof Select>>;
}

const CurrencyInput: React.FC<CurrencyInputProps> = (props) => {
  const {
    view = false,
    disabled,
    showCurrency = true,
    currencyOptions = objectTransenum(currencyEnum),
    precision = 2,
    toFixed = 2,
    currencyBeforeAmount = false,
    color = 'yellow',
    highlight = true,
    showUnitIndicator = true,
    unitIndicatorAlign,
    wrapperProps = {},
    className,
    inputProps = {},
    selectProps = {},
    onCurrencyChange,
  } = props;

  const [state, setState] = useControllableValue<CurrencyInputValue>(props, {
    defaultValue: {} as CurrencyInputValue,
  });

  const amount = state?.value;
  const currency = state?.currency;

  const handleInputChange = useMemoizedFn(
    (v: number | string | null | undefined) => {
      setState({ ...state, value: v ?? undefined });
    }
  );

  const handleSelectChange = useMemoizedFn((c: string) => {
    setState({ ...state, currency: c });
    onCurrencyChange?.(c);
  });

  /** unit 单位（复刻 FieldCurrency useUnitIndicator 阈值匹配） */
  const unitIndicator = useMemo(() => {
    if (
      !showUnitIndicator ||
      typeof amount !== 'number' ||
      Number.isNaN(amount)
    )
      return '';
    const abs = Math.abs(amount);
    for (const t of UNIT_THRESHOLDS) {
      if (abs >= t.value) return t.label;
    }
    return '';
  }, [showUnitIndicator, amount]);

  /** 编辑态金额展示文本（供气泡测量锚点，与 InputNumber formatter 输出一致） */
  const editDisplayValue = useMemo(
    () => formatEditAmount(amount, toFixed),
    [amount, toFixed]
  );

  const { className: wrapperClassName, ...restWrapperProps } = wrapperProps;

  // ── 只读态 ──
  if (view) {
    const currencyText = currency ? getCurrencyName(currency) : '';
    const amountText = formatAmount(amount, precision);
    return (
      <div
        className={cx(
          'sform-item-currency-type',
          'wrapper-view',
          color,
          className,
          wrapperClassName
        )}
        {...restWrapperProps}
      >
        <UnitIndicatorWrapper
          type="read"
          content={unitIndicator}
          show={showUnitIndicator}
          align={unitIndicatorAlign}
        >
          <span
            className={cx('currency-type-text', {
              'currency-type-value': currencyText,
            })}
            style={{ fontWeight: highlight ? 700 : 400 }}
          >
            {currencyBeforeAmount && showCurrency && currencyText
              ? `${currencyText} `
              : ''}
            {amountText}
            {!currencyBeforeAmount && showCurrency && currencyText
              ? ` ${currencyText}`
              : ''}
          </span>
        </UnitIndicatorWrapper>
      </div>
    );
  }

  // ── 编辑态 ──
  /** 币种 Select：compact 组合用 bordered=true（合并边框），addon 嵌入用 bordered=false */
  const renderCurrencySelect = (bordered: boolean) => (
    <Select
      className="shrink-0"
      value={currency?.toLocaleLowerCase()}
      onChange={handleSelectChange}
      options={currencyOptions}
      disabled={selectProps.disabled ?? disabled}
      bordered={bordered}
      style={{
        width: CURRENCY_SELECT_WIDTH,
        ...(selectProps.style ?? {}),
      }}
      {...selectProps}
    />
  );

  /** 金额 InputNumber：币种前置（compact）时以 wrapperClassName 撑满 group */
  const renderAmountInput = (opts: {
    withAfterSelect: boolean;
    fillGroup?: boolean;
  }) => (
    <InputNumber
      direction="vertical"
      max={Number.MAX_SAFE_INTEGER - 1}
      min={0}
      value={amount ?? ''}
      toFixed={toFixed}
      autoComplete="off"
      formatter={(v: any) => formatEditAmount(v, toFixed)}
      parser={(v: any) => String(v ?? '').replace(/,/g, '')}
      disabled={inputProps.disabled ?? disabled}
      onChange={handleInputChange}
      className={cx('min-w-0 flex-1', inputProps.className)}
      wrapperClassName={opts.fillGroup ? 'min-w-0 flex-1' : undefined}
      // 币种后置：Select 以 addonAfter 嵌进 InputNumber 边框（镜像 DurationInput addonAfter 范式；
      // 注意 direction=vertical 的内部 ↑↓ 步进按钮会被 addon 覆盖）
      addonAfter={
        opts.withAfterSelect && showCurrency
          ? renderCurrencySelect(false)
          : undefined
      }
      {...inputProps}
    />
  );

  return (
    <div
      className={cx(
        'sform-item-currency-type',
        // 币种前置（currencyBeforeAmount）时金额输入区在右侧，内容右对齐；
        // .rock-input 是 bedrock InputNumber 内部 input 元素类（本仓 CurrencyType.less 已依赖 rock- 前缀），
        // 不用 [&_input] 以免误伤币种 Select 内部 input（Select 用 rock-select-* 前缀）
        currencyBeforeAmount &&
          '[&_.rock-input]:text-right [&_.rock-input]:pr-3',
        className,
        wrapperClassName
      )}
      {...restWrapperProps}
    >
      <div className="flex w-full items-start">
        <UnitIndicatorWrapper
          type="edit"
          content={unitIndicator}
          show={showUnitIndicator}
          bubbleAnchor={currencyBeforeAmount ? 'right' : undefined}
          measureText={editDisplayValue}
        >
          {currencyBeforeAmount ? (
            // 币种前置：以 Select 为主、InputNumber 为右，Input.Group compact 合并为统一带边框整体
            // （镜像 InputPrice 结构；compact CSS 已支持 .rock-input-number-wrapper 边框合并）
            <Input.Group compact className="w-full">
              {renderCurrencySelect(true)}
              {renderAmountInput({ withAfterSelect: false, fillGroup: true })}
            </Input.Group>
          ) : (
            // 币种后置（默认）：InputNumber + addonAfter Select 嵌边（现状不变）
            renderAmountInput({ withAfterSelect: true })
          )}
        </UnitIndicatorWrapper>
      </div>
    </div>
  );
};

export default CurrencyInput;
